import { useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import api from '../../config/api';
import { useFeatures } from '../../hooks/useFeatures';

const DOC_TYPES = {
  100: 'Purchase In', 120: 'Purchase Return',
  200: 'Sales Out',   220: 'Sales Return',
  230: 'Proforma',    300: 'Inventory Count', 400: 'Loss/Damage'
};

export default function AccountStatement() {
  const { id } = useParams();
  const navigate = useNavigate();
  const printRef = useRef();

  const features = useFeatures();
  const { data: settings } = useQuery({ queryKey: ['settings'], queryFn: () => api.get('/api/settings').then(r => r.data) });
  const RATE_FALLBACK = 89500;
  const rate = Number(settings?.exchange_rate) || RATE_FALLBACK;
  // base currency = USD. Convert each doc/payment to USD at its own snapshot rate
  // (identity for USD, so single-currency accounts are unaffected).
  const toUSD = (amt, cur, r) => cur === 'LBP' ? (Number(amt) || 0) / (Number(r) || rate) : (Number(amt) || 0);
  const fmtMoney = (amt, cur) => cur === 'LBP'
    ? `${Math.round(Number(amt) || 0).toLocaleString('en-US')} LBP`
    : `$${(Number(amt) || 0).toFixed(2)}`;

  const [dateFrom, setDateFrom] = useState('');
  const [dateTo,   setDateTo]   = useState('');

  const { data: account } = useQuery({
    queryKey: ['account', id],
    queryFn: () => api.get(`/api/accounts/${id}`).then(r => r.data),
  });

  // Docs within selected period
  const { data: docs = [] } = useQuery({
    queryKey: ['account-docs-stmt', id, dateFrom, dateTo],
    queryFn: () => api.get('/api/documents', {
      params: {
        account_id: id,
        status: 'confirmed',
        all: true,
        ...(dateFrom && { date_from: dateFrom }),
        ...(dateTo   && { date_to:   dateTo   }),
      }
    }).then(r => Array.isArray(r.data) ? r.data : (r.data?.data || [])),
    enabled: !!id,
  });

  // Docs BEFORE dateFrom for opening balance
  const { data: prevDocs = [] } = useQuery({
    queryKey: ['account-docs-prev', id, dateFrom],
    queryFn: () => api.get('/api/documents', {
      params: {
        account_id: id,
        status: 'confirmed',
        all: true,
        date_to: new Date(new Date(dateFrom).getTime() - 86400000).toISOString().split('T')[0],
      }
    }).then(r => Array.isArray(r.data) ? r.data : (r.data?.data || [])),
    enabled: !!id && !!dateFrom,
  });

  // Payments for period docs
  const { data: payments = [] } = useQuery({
    queryKey: ['account-payments-stmt', id, docs.map(d => d._id).join(',')],
    queryFn: async () => {
      if (docs.length === 0) return [];
      const results = await Promise.all(
        docs.map(d =>
          api.get(`/api/documents/${d._id}/payments`).then(r =>
            r.data.map(p => ({ ...p, document_number: d.number, doc_type: d.type }))
          )
        )
      );
      return results.flat();
    },
    enabled: docs.length > 0,
  });

  // Payments for prev docs
  const { data: prevPayments = [] } = useQuery({
    queryKey: ['account-payments-prev', id, prevDocs.map(d => d._id).join(',')],
    queryFn: async () => {
      if (prevDocs.length === 0) return [];
      const results = await Promise.all(
        prevDocs.map(d => api.get(`/api/documents/${d._id}/payments`).then(r => r.data.map(p => ({ ...p, document_number: d.number, doc_type: d.type }))))
      );
      return results.flat();
    },
    enabled: prevDocs.length > 0,
  });

  // Opening balance calculation
  // 1. Account's own opening_balance (if date filter is after opening_balance_date)
  const accountOpeningBal = (() => {
    if (!account?.opening_balance) return 0;
    if (!dateFrom) return 0; // no filter — will be shown as first line instead
    const obDate = account.opening_balance_date ? new Date(account.opening_balance_date) : null;
    if (!obDate || obDate < new Date(dateFrom)) {
      // opening balance date is before our filter start — include it
      return account.opening_balance_type === 'credit'
        ? -account.opening_balance
        :  account.opening_balance;
    }
    return 0;
  })();

  // 2. Transactions before dateFrom
  // Direction, seen from this account (positive balance = they owe us):
  //   sale +, sale return −, purchase − (we owe them), purchase return +. Proformas, stock
  //   counts and losses are not money owed, so they are left out. A payment moves the balance
  //   the other way of its document (a refund is a negative payment, so it flips by itself).
  const SIGN = { 200: 1, 220: -1, 100: -1, 120: 1 };
  const docEffect = (d) => (SIGN[d.type] || 0) * toUSD(d.totals?.total || 0, d.currency, d.exchange_rate_snapshot);
  const payEffect = (p) => -(SIGN[p.doc_type] || 0) * toUSD(p.amount, p.currency, p.exchange_rate_snapshot);
  // a payment belongs to the period of the day it was PAID (not of its invoice)
  const fromT = dateFrom ? new Date(dateFrom + 'T00:00:00').getTime() : -Infinity;
  const toT   = dateTo   ? new Date(dateTo + 'T23:59:59.999').getTime() : Infinity;
  const allPayments = [...payments, ...prevPayments].filter(p => p.status === 'completed' && SIGN[p.doc_type]);
  const payT = (p) => new Date(p.createdAt).getTime();

  const txOpeningBal = dateFrom
    ? prevDocs.filter(d => SIGN[d.type]).reduce((s, d) => s + docEffect(d), 0)
      + allPayments.filter(p => payT(p) < fromT).reduce((s, p) => s + payEffect(p), 0)
    : 0;

  const openingBalance = accountOpeningBal + txOpeningBal;

  // Build statement lines
  const lines = [];

  // If no date filter and account has opening balance — show it as first line
  if (!dateFrom && account?.opening_balance) {
    lines.push({
      date: account.opening_balance_date ? new Date(account.opening_balance_date) : new Date(0),
      reference: '—',
      description: 'Opening Balance',
      debit:  account.opening_balance_type !== 'credit' ? account.opening_balance : 0,
      credit: account.opening_balance_type === 'credit' ? account.opening_balance : 0,
      _type: 'opening',
    });
  }

  docs.filter(d => SIGN[d.type]).forEach(d => {
    const cur = d.currency || 'USD';
    const e = docEffect(d);                     // USD-equivalent for the running balance
    lines.push({
      date: new Date(d.date),
      reference: d.number || '—',
      description: DOC_TYPES[d.type] || '—',
      debit: e > 0 ? e : 0,
      credit: e < 0 ? -e : 0,
      origAmount: d.totals?.total || 0,
      currency: cur,
      _type: 'document',
    });
  });

  allPayments.filter(p => payT(p) >= fromT && payT(p) <= toT).forEach(p => {
    const cur = p.currency || 'USD';
    const e = payEffect(p);
    lines.push({
      date: new Date(p.createdAt),
      reference: p.document_number,
      description: `${p.is_refund ? 'Refund' : 'Payment'} — ${p.payment_method_name}${p.notes ? ' (' + p.notes + ')' : ''}`,
      debit: e > 0 ? e : 0,
      credit: e < 0 ? -e : 0,
      origAmount: p.amount,
      currency: cur,
      _type: 'payment',
    });
  });

  // per-currency summary (raw, never converted)
  const byCurrency = {};
  docs.filter(d => SIGN[d.type]).forEach(d => { const c = d.currency || 'USD'; (byCurrency[c] ||= { currency: c, invoiced: 0, paid: 0 }).invoiced += SIGN[d.type] * (d.totals?.total || 0); });
  allPayments.filter(p => payT(p) >= fromT && payT(p) <= toT).forEach(p => { const c = p.currency || 'USD'; (byCurrency[c] ||= { currency: c, invoiced: 0, paid: 0 }).paid += SIGN[p.doc_type] * (Number(p.amount) || 0); });

  lines.sort((a, b) => a.date - b.date);

  // Running balance
  let running = dateFrom ? openingBalance : 0;
  const stmtLines = lines.map(l => {
    running += l.debit - l.credit;
    return { ...l, balance: running };
  });

  const periodDebit  = lines.filter(l => l._type !== 'opening').reduce((s, l) => s + l.debit,  0);
  const periodCredit = lines.filter(l => l._type !== 'opening').reduce((s, l) => s + l.credit, 0);
  const closingBalance = (dateFrom ? openingBalance : (account?.opening_balance_type !== 'credit' ? (account?.opening_balance || 0) : -(account?.opening_balance || 0))) + periodDebit - periodCredit;

  const fmtDate = (d) => new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  const fmtAmt  = (n) => n > 0 ? `$${n.toFixed(2)}` : '—';

  const rowBg = (type) => {
    if (type === 'opening') return 'bg-yellow-50/60 italic';
    if (type === 'payment') return 'bg-green-50/20';
    return '';
  };

  const handlePrint = () => {
    const win = window.open('', '_blank');
    win.document.write(`
      <html><head><title>Statement — ${account?.name}</title>
      <style>
        body { font-family: Arial, sans-serif; font-size: 12px; padding: 30px; color: #111; }
        h2 { margin: 0 0 4px; font-size: 18px; }
        p { margin: 2px 0; color: #555; }
        table { width: 100%; border-collapse: collapse; margin-top: 16px; }
        th { background: #f3f4f6; border-bottom: 2px solid #ddd; padding: 7px 10px; text-align: left; font-size: 11px; text-transform: uppercase; }
        td { border-bottom: 1px solid #eee; padding: 6px 10px; }
        .text-right { text-align: right; }
        .opening { background: #fffbeb; font-style: italic; color: #92400e; }
        .payment { background: #f0fdf4; }
        tfoot td { border-top: 2px solid #ddd; font-weight: bold; background: #f9fafb; }
      </style>
      </head><body>${printRef.current.innerHTML}</body></html>
    `);
    win.document.close();
    win.print();
  };

  const handleExportCSV = () => {
    const rows = [['Date', 'Reference', 'Description', 'Debit', 'Credit', 'Balance']];
    if (dateFrom && openingBalance !== 0) {
      rows.push([fmtDate(dateFrom), '—', 'Opening Balance', '', '', openingBalance.toFixed(2)]);
    }
    stmtLines.forEach(l => {
      rows.push([
        fmtDate(l.date), l.reference, l.description,
        l.debit  > 0 ? l.debit.toFixed(2)  : '',
        l.credit > 0 ? l.credit.toFixed(2) : '',
        l.balance.toFixed(2)
      ]);
    });
    rows.push(['', '', 'Closing Balance', periodDebit.toFixed(2), periodCredit.toFixed(2), closingBalance.toFixed(2)]);
    const csv = rows.map(r => r.map(c => `"${c}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href = url;
    a.download = `statement_${account?.name}_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="p-3 md:p-6 text-gray-800">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(`/accounts/${id}/ledger`)}
            className="text-gray-500 hover:text-gray-700 text-sm font-medium">← Ledger</button>
          <div>
            <h1 className="text-xl md:text-2xl font-bold text-gray-900">Statement of Account</h1>
            <p className="text-gray-500 text-sm">{account?.name}</p>
          </div>
        </div>
        <div className="flex gap-2">
          <button onClick={handlePrint}
            className="flex items-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-800 text-white text-sm font-medium rounded-lg">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
            </svg>
            Print
          </button>
          <button onClick={handleExportCSV}
            className="flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium rounded-lg">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            Export CSV
          </button>
        </div>
      </div>

      {/* Date filter */}
      <div className="bg-white border border-gray-200 rounded-xl p-4 mb-5 flex flex-wrap gap-3 items-center">
        <span className="text-sm text-gray-600 font-medium">Period:</span>
        <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
          className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none" />
        <span className="text-sm text-gray-400">to</span>
        <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
          className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none" />
        {(dateFrom || dateTo) && (
          <button onClick={() => { setDateFrom(''); setDateTo(''); }}
            className="text-sm text-blue-500 hover:text-blue-700">Clear</button>
        )}
        {dateFrom && (
          <span className="ml-auto text-xs text-gray-400 bg-yellow-50 border border-yellow-200 rounded px-2 py-1">
            Opening balance: ${openingBalance.toFixed(2)} (transactions before {fmtDate(dateFrom)})
          </span>
        )}
      </div>

      {/* per-currency summary (raw amounts per currency) */}
      {features.second_currency && Object.keys(byCurrency).length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-5">
          {Object.values(byCurrency).map(c => (
            <div key={c.currency} className="bg-white border border-gray-200 rounded-xl p-4">
              <div className="font-bold text-gray-800 mb-1.5">{c.currency}</div>
              <div className="flex justify-between text-sm"><span className="text-gray-500">Invoiced</span><span className="font-semibold">{fmtMoney(c.invoiced, c.currency)}</span></div>
              <div className="flex justify-between text-sm"><span className="text-gray-500">Paid</span><span className="font-semibold text-green-600">{fmtMoney(c.paid, c.currency)}</span></div>
              <div className="flex justify-between text-sm border-t border-gray-100 mt-1.5 pt-1.5"><span className="text-gray-500">Open</span><span className={`font-bold ${(c.invoiced - c.paid) > 0 ? 'text-red-500' : 'text-green-600'}`}>{fmtMoney(c.invoiced - c.paid, c.currency)}</span></div>
            </div>
          ))}
        </div>
      )}

      {/* Printable content (table hidden on mobile — cards shown below instead) */}
      <div ref={printRef} className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 md:p-6">
        <div className="mb-6">
          <h2 className="text-xl font-bold text-gray-900">Statement of Account</h2>
          <div className="text-gray-500 text-sm mt-1">
            <p><strong>Account:</strong> {account?.name} — <span className="capitalize">{account?.type}</span></p>
            {account?.phone && <p><strong>Phone:</strong> {account.phone}</p>}
            {account?.email && <p><strong>Email:</strong> {account.email}</p>}
            <p className="mt-1">
              <strong>Period:</strong>{' '}
              {dateFrom ? fmtDate(dateFrom) : 'All time'} — {dateTo ? fmtDate(dateTo) : 'Today'}
            </p>
            <p><strong>Printed:</strong> {fmtDate(new Date())}</p>
          </div>
        </div>

        {stmtLines.length === 0 && !dateFrom ? (
          <p className="text-gray-400 text-center py-10">No transactions found.</p>
        ) : (
          <table className="w-full text-sm hidden md:table">
            <thead>
              <tr className="bg-gray-50 border-b-2 border-gray-200 text-xs uppercase tracking-wider text-gray-500">
                <th className="text-left px-3 py-3 font-semibold">Date</th>
                <th className="text-left px-3 py-3 font-semibold">Reference</th>
                <th className="text-left px-3 py-3 font-semibold">Description</th>
                <th className="text-right px-3 py-3 font-semibold text-red-500">Debit</th>
                <th className="text-right px-3 py-3 font-semibold text-green-600">Credit</th>
                <th className="text-right px-3 py-3 font-semibold">Balance</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {/* Opening balance row when date filter applied */}
              {dateFrom && (
                <tr className="bg-yellow-50/50 italic">
                  <td className="px-3 py-2.5 text-gray-400 text-xs">{fmtDate(dateFrom)}</td>
                  <td className="px-3 py-2.5 text-gray-400">—</td>
                  <td className="px-3 py-2.5 text-gray-600 font-medium">Opening Balance</td>
                  <td className="px-3 py-2.5 text-right">—</td>
                  <td className="px-3 py-2.5 text-right">—</td>
                  <td className={`px-3 py-2.5 text-right font-bold ${openingBalance <= 0 ? 'text-green-600' : 'text-orange-500'}`}>
                    ${openingBalance.toFixed(2)}
                  </td>
                </tr>
              )}

              {stmtLines.map((l, idx) => (
                <tr key={idx} className={`hover:bg-gray-50/50 ${rowBg(l._type)}`}>
                  <td className="px-3 py-2.5 text-gray-500 text-xs whitespace-nowrap">{fmtDate(l.date)}</td>
                  <td className="px-3 py-2.5 font-mono text-blue-600 font-semibold text-xs">{l.reference}</td>
                  <td className="px-3 py-2.5 text-gray-700">{l.description}</td>
                  <td className="px-3 py-2.5 text-right font-medium text-red-500">{l.debit > 0 ? fmtMoney(l.origAmount ?? l.debit, l.currency) : '—'}</td>
                  <td className="px-3 py-2.5 text-right font-medium text-green-600">{l.credit > 0 ? fmtMoney(l.origAmount ?? l.credit, l.currency) : '—'}</td>
                  <td className={`px-3 py-2.5 text-right font-bold ${l.balance <= 0 ? 'text-green-600' : 'text-red-500'}`}>
                    ${l.balance.toFixed(2)}
                  </td>
                </tr>
              ))}

              {stmtLines.length === 0 && dateFrom && (
                <tr><td colSpan={6} className="px-3 py-8 text-center text-gray-400">No transactions in this period.</td></tr>
              )}
            </tbody>
            <tfoot className="bg-gray-50 border-t-2 border-gray-300 text-sm font-bold">
              <tr>
                <td colSpan={3} className="px-3 py-3 text-gray-700">
                  Closing Balance
                  {dateFrom && openingBalance !== 0 && (
                    <span className="font-normal text-gray-400 text-xs ml-2">
                      (Opening ${openingBalance.toFixed(2)} + ${periodDebit.toFixed(2)} − ${periodCredit.toFixed(2)})
                    </span>
                  )}
                </td>
                <td className="px-3 py-3 text-right text-red-500">${periodDebit.toFixed(2)}</td>
                <td className="px-3 py-3 text-right text-green-600">${periodCredit.toFixed(2)}</td>
                <td className={`px-3 py-3 text-right text-base ${closingBalance <= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  ${closingBalance.toFixed(2)}
                </td>
              </tr>
            </tfoot>
          </table>
        )}
      </div>

      {/* mobile card view — OUTSIDE printRef so print only includes the table */}
      {(stmtLines.length > 0 || dateFrom) && (
        <div className="md:hidden space-y-2 mt-3">
            {dateFrom && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 flex justify-between items-center text-sm italic">
                <span className="text-gray-600 font-medium">Opening Balance</span>
                <span className={`font-bold ${openingBalance <= 0 ? 'text-green-600' : 'text-orange-500'}`}>${openingBalance.toFixed(2)}</span>
              </div>
            )}
          {stmtLines.map((l, idx) => (
            <div key={idx} className={`border border-gray-200 rounded-lg p-3 ${l._type === 'payment' ? 'bg-green-50/40' : l._type === 'opening' ? 'bg-yellow-50/60' : 'bg-white'}`}>
              <div className="flex justify-between items-center mb-1">
                <span className="font-mono text-blue-600 font-semibold text-xs">{l.reference}</span>
                <span className="text-xs text-gray-400">{fmtDate(l.date)}</span>
              </div>
              <div className="text-sm text-gray-700 mb-1.5">{l.description}</div>
              <div className="flex justify-between text-xs">
                <span className="text-red-500">Debit <b>{l.debit > 0 ? fmtMoney(l.origAmount ?? l.debit, l.currency) : '—'}</b></span>
                <span className="text-green-600">Credit <b>{l.credit > 0 ? fmtMoney(l.origAmount ?? l.credit, l.currency) : '—'}</b></span>
                <span className={`font-bold ${l.balance <= 0 ? 'text-green-600' : 'text-red-500'}`}>Bal ${l.balance.toFixed(2)}</span>
              </div>
            </div>
          ))}
          {stmtLines.length === 0 && dateFrom && (
            <div className="text-center text-gray-400 text-sm py-6">No transactions in this period.</div>
          )}
          <div className="bg-gray-50 border-2 border-gray-200 rounded-lg p-3 text-sm font-bold flex justify-between items-center">
            <span className="text-gray-700">Closing Balance</span>
            <span className={closingBalance <= 0 ? 'text-green-600' : 'text-red-600'}>${closingBalance.toFixed(2)}</span>
          </div>
        </div>
      )}
    </div>
  );
}
